'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';

import { SwipeHandle } from '@workspace/ui/components/swipe-handle';
import { useIsTouchDevice } from '@workspace/ui/hooks/use-touch-device';
import { cn } from '@workspace/ui/lib/utils';

// Lets DialogContent close the dialog from the swipe handle; Radix doesn't expose its own close.
const DialogCloseContext = React.createContext<(() => void) | null>(null);

// Closes the nearest Dialog (or Sheet, which is built on it); null outside one.
const useDialogClose = () => React.useContext(DialogCloseContext);

function Dialog({
  open: openProp,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) setUncontrolledOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [isControlled, onOpenChange],
  );
  const close = React.useCallback(() => handleOpenChange(false), [handleOpenChange]);

  return (
    <DialogCloseContext.Provider value={close}>
      <DialogPrimitive.Root
        data-slot="dialog"
        open={open}
        onOpenChange={handleOpenChange}
        {...props}
      />
    </DialogCloseContext.Provider>
  );
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'z-30 fixed inset-0 bg-black/15 backdrop-blur-sm data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  hideCloseBtn = false,
  overlayClassName = '',
  closeBtnClassName = '',
  swipeToClose = true,
  mobileHeight,
  style,
  ref,
  onOpenAutoFocus,
  'aria-describedby': ariaDescribedBy,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  hideCloseBtn?: boolean;
  overlayClassName?: string;
  closeBtnClassName?: string;
  // Phones only: show a handle that closes the sheet on a downward swipe.
  swipeToClose?: boolean;
  // Phones only: fixed sheet height as a fraction of the visible screen. Default fits the content.
  mobileHeight?: number;
}) {
  const close = React.useContext(DialogCloseContext);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const isTouchDevice = useIsTouchDevice();

  const setContentRef = (node: HTMLDivElement | null) => {
    contentRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  // On a touch screen the first field would raise the keyboard over the dialog before anyone
  // tapped it, so focus the dialog itself instead.
  const handleOpenAutoFocus = (event: Event) => {
    onOpenAutoFocus?.(event);
    if (isTouchDevice && !event.defaultPrevented) {
      event.preventDefault();
      contentRef.current?.focus();
    }
  };

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={setContentRef}
        data-slot="dialog-content"
        className={cn(
          'z-50 fixed gap-4 grid bg-background shadow-100 p-4 border sm:rounded-xl rounded-t-xl rounded-b-none w-full max-w-full sm:max-w-lg max-h-[90dvh] sm:max-h-[80dvh] overflow-y-auto duration-200',
          // Mobile: Bottom sheet style, lifted above the on-screen keyboard
          'max-sm:bottom-[var(--keyboard-inset,0px)] max-sm:inset-x-0 translate-x-0 translate-y-0',
          'max-sm:max-h-[calc(var(--visual-height,100dvh)*0.9)]',
          mobileHeight &&
            'max-sm:h-[calc(var(--visual-height,100dvh)*var(--dialog-mobile-height))]',
          'max-sm:data-[state=open]:animate-in max-sm:data-[state=open]:slide-in-from-bottom-full',
          'max-sm:data-[state=closed]:animate-out max-sm:data-[state=closed]:slide-out-to-bottom-full',
          // Desktop: Centered modal
          'sm:top-[50%] sm:left-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:data-[state=open]:animate-in sm:data-[state=closed]:animate-out sm:data-[state=closed]:fade-out-0 sm:data-[state=open]:fade-in-0 sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95',
          className,
        )}
        style={
          {
            ...(mobileHeight && { '--dialog-mobile-height': mobileHeight }),
            ...style,
          } as React.CSSProperties
        }
        aria-describedby={ariaDescribedBy ?? undefined}
        onOpenAutoFocus={handleOpenAutoFocus}
        {...props}
      >
        {swipeToClose && close && <SwipeHandle contentRef={contentRef} onClose={close} />}
        {!hideCloseBtn && (
          <DialogPrimitive.Close
            className={cn(
              'group top-4 sm:top-4 right-4 z-10 absolute flex justify-center items-center border-[1.3px] border-gray-30 hover:border-primary-darker focus:border-primary-darker rounded-full focus:outline-none size-5 disabled:pointer-events-none transition-all',
              // 40px tap area around the 20px button
              "before:absolute before:-inset-2.5 before:content-['']",
              closeBtnClassName,
            )}
          >
            <XIcon
              strokeWidth={2}
              className="size-3.5 text-gray-30 group-hover:text-primary-darker group-focus:text-primary-darker transition-all"
            />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="dialog-header" className={cn('flex flex-col gap-1', className)} {...props} />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex sm:flex-row flex-col-reverse sm:justify-end gap-2', className)}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('pe-6 font-semibold text-lg leading-none', className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  useDialogClose,
};
